import { z } from 'zod';
import { AccentColor } from './firm-appearance.js';
import { TenantId } from './ids.js';
/** A firm the signed-in person belongs to. */
export const FirmSummary = z
    .object({
    tenantId: TenantId,
    name: z.string(),
})
    .meta({ id: 'FirmSummary' });
/** `GET /api/v1/me`: who is signed in, in which firm, and the CSRF token for writes. */
export const MeResponse = z
    .object({
    app: z.enum(['firm', 'client', 'platform']),
    /** The account, by its Keycloak `sub` - e.g. to tell one's own follow-ups from others'. */
    subject: z.string(),
    displayName: z.string().nullable(),
    firstName: z
        .string()
        .nullable()
        .describe('The given name the sign-in keeps (ADR-058) - what the profile form starts from.'),
    lastName: z.string().nullable().describe('The family name the sign-in keeps (ADR-058).'),
    email: z.email().nullable(),
    emailVerified: z
        .boolean()
        .describe('Whether the sign-in confirmed the address by a link; `false` when unsure.'),
    firms: z.array(FirmSummary),
    activeTenantId: TenantId.nullable(),
    accentColor: AccentColor.nullable().describe("The active firm's colour for the surfaces; `null`: the Kanos colours."),
    firmAdmin: z
        .boolean()
        .describe('Administers the active firm - a hint for the UI; the API checks every write itself.'),
    firmProfessional: z
        .boolean()
        .describe('A licensed professional of the active firm in Kanos (ADR-025) - a hint for the UI.'),
    firmCaseWorker: z
        .boolean()
        .describe('Does case work in the active firm - sees risk assessments and identity data, unlike the office alone (ADR-043). A hint for the UI.'),
    stepUpValidUntil: z.iso
        .datetime({ offset: true })
        .nullable()
        .describe('Until when the last second factor confirms step-up actions (ADR-005); `null`: none fresh. A hint, so the UI asks for it before a long form rather than after it.'),
    csrfToken: z
        .string()
        .min(1)
        .describe('Send as `X-CSRF-Token` with every state-changing request.'),
    uploadMaxBytes: z
        .int()
        .positive()
        .describe('The largest file an upload may carry, in bytes (ADR-011 §1) - set by the operator. A hint, so the UI refuses a larger file before sending it; the API checks every upload itself.'),
})
    .meta({ id: 'MeResponse' });
/** `PUT /api/v1/me/active-firm`. */
export const ActiveFirmRequest = z.object({ tenantId: TenantId }).meta({ id: 'ActiveFirmRequest' });
/**
 * What a signed-in person does with the own account at the identity provider (ADR-005), each
 * as an application-initiated action of Keycloak: add a passkey, add an authenticator app,
 * change the password, change name and e-mail address.
 */
export const AccountAction = z
    .enum(['passkey', 'otp', 'password', 'profile'])
    .meta({ id: 'AccountAction' });
/**
 * A way of the signed-in person to sign in (ADR-033): the password, an authenticator app, a
 * passkey - or a security key kept as second factor from before passkeys.
 */
export const CredentialKind = z
    .enum(['password', 'otp', 'passkey', 'security-key'])
    .meta({ id: 'CredentialKind' });
export const OwnCredential = z
    .object({
    id: z.string(),
    kind: CredentialKind,
    /** The name the person gave it, e.g. "iPhone"; `null`: none. */
    label: z.string().nullable(),
    createdAt: z.iso.datetime({ offset: true }).nullable(),
    /** Whether the identity provider lets it be removed - a password cannot be. */
    removable: z.boolean(),
})
    .meta({ id: 'OwnCredential' });
export const OwnCredentials = z.array(OwnCredential).meta({ id: 'OwnCredentials' });
/** The own name, changed in the profile (ADR-058). */
export const OwnNameInput = z
    .object({
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().min(1).max(100),
})
    .meta({ id: 'OwnNameInput' });
/**
 * A new own password (ADR-058): at least 12 characters - the identity provider's policy checks
 * the rest - and whether every other device is signed out with it.
 */
export const OwnPasswordInput = z
    .object({
    password: z.string().min(12).max(128),
    signOutOthers: z.boolean(),
})
    .meta({ id: 'OwnPasswordInput' });
/** `GET /auth/login`: where to return after signing in - a path of the app, nothing else. */
export const LoginQuery = z
    .object({
    returnTo: z
        .string()
        .optional()
        .describe('Path of the app to return to; anything else falls back to `/`.'),
    stepUp: z
        .literal('1')
        .optional()
        .describe('Confirm the signed-in person with a fresh second factor (step-up, ADR-005) instead of signing in anew.'),
    remove: z
        .uuid()
        .optional()
        .describe('Let the signed-in person remove one of the own ways in (`GET /api/v1/me/credentials`) at the identity provider, which asks for confirmation (ADR-033); wins over `setup`.'),
    setup: AccountAction.optional().describe('Let the signed-in person manage the own account at the identity provider (ADR-005): add a passkey or an authenticator app, change the password or name and address; wins over `stepUp`.'),
    signup: z
        .literal('1')
        .optional()
        .describe('Client portal: show the registration of the realm `mandant` instead of the sign-in (ADR-026).'),
    handover: z
        .string()
        .max(4096)
        .optional()
        .describe('Set by the API itself when a firm portal domain sends the login to the central portal (ADR-026); sealed, never made by a client.'),
})
    .meta({ id: 'LoginQuery' });
/** `GET /auth/handover`: the one-time code that brings a login to a firm's portal domain. */
export const HandoverQuery = z
    .object({ code: z.string().regex(/^[A-Za-z0-9_-]{43}$/) })
    .meta({ id: 'HandoverQuery' });
/** `POST /auth/logout`: where the browser goes to end single sign-on as well. */
export const LogoutResponse = z
    .object({ endSessionUrl: z.url().nullable() })
    .meta({ id: 'LogoutResponse' });
/** `GET /health/live` and `/health/ready`. */
export const HealthStatus = z.object({ status: z.literal('ok') }).meta({ id: 'HealthStatus' });
/** Browsers and systems a session is told apart by - coarse, from the User-Agent (ADR-005). */
export const DeviceBrowser = z
    .enum(['chrome', 'edge', 'firefox', 'safari', 'opera', 'samsung'])
    .meta({ id: 'DeviceBrowser' });
export const DeviceSystem = z
    .enum(['windows', 'macos', 'ios', 'ipados', 'android', 'linux', 'chromeos'])
    .meta({ id: 'DeviceSystem' });
/** A session of the signed-in person in the same app - this one or another device. */
/** Roughly where a device was at sign-in (ADR-059): a city, if known, and the country. */
export const SessionLocation = z
    .object({
    /** As the database names it, e.g. „Nuremberg“; `null`: only the country is known. */
    city: z.string().nullable(),
    /** ISO 3166-1 alpha-2, e.g. `DE`. */
    country: z.string().regex(/^[A-Z]{2}$/),
})
    .meta({ id: 'SessionLocation' });
export const OwnSession = z
    .object({
    id: z.uuid(),
    /** `null`: not recognised - never guessed. */
    browser: DeviceBrowser.nullable(),
    system: DeviceSystem.nullable(),
    location: SessionLocation.nullable().describe('Roughly where the device was at sign-in, from its IP address (ADR-059); `null`: unknown.'),
    createdAt: z.iso.datetime({ offset: true }),
    lastSeenAt: z.iso.datetime({ offset: true }),
    /** The session of the request. */
    current: z.boolean(),
})
    .meta({ id: 'OwnSession' });
export const OwnSessionList = z.array(OwnSession).meta({ id: 'OwnSessionList' });
export const OwnSessionParams = z.object({ sessionId: z.uuid() }).meta({ id: 'OwnSessionParams' });
/**
 * How long the current session still runs (ADR-005), in seconds from the answer - the browser
 * warns before it ends. Seconds rather than times: the browser's clock may be off.
 */
export const SessionTimeouts = z
    .object({
    /** Until the idle timeout, if nobody works in the meantime. */
    idleSeconds: z.int().nonnegative(),
    /** Until the absolute end, which no activity moves. */
    absoluteSeconds: z.int().nonnegative(),
})
    .meta({ id: 'SessionTimeouts' });
/**
 * Marks a request the browser sends on its own (polling, refetch on focus): it does not count
 * as activity, so a tab left open still reaches the idle timeout (ADR-005).
 */
export const BACKGROUND_HEADER = 'X-Kanos-Background';
//# sourceMappingURL=session.js.map